import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material'
import MuiButton from 'gui/components/common/MuiButton'
import Link from 'gui/components/link'
import { CONTENT_ROOTS } from 'services/content'
import { CONTENT_TYPE_VIDEO } from 'services/content/content-types'
import {
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE,
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_OTHER,
} from 'services/content-references/types'
import './course-module-content-section.scss'

interface CourseModuleContentCardProps {
  contentId?: string
  contentTitle: string
  contentIdNumber?: number
  contentType?: string
  contentReferenceType?: string
  thumbnailUrl: string | null
  published?: boolean
  referenceId: string
  sortOrder: number
  editedSortKey?: string
  disabled: boolean
  onSortKeyChange: (id: string, value: string) => void
  onConfirmSortKey: (id: string) => void
  onCancelSortKey: (id: string) => void
  onRemove: (id: string) => void
}

/**
 * Individual content card component
 */
function CourseModuleContentCard ({
  contentId,
  contentTitle,
  contentIdNumber,
  contentType,
  contentReferenceType,
  thumbnailUrl,
  published,
  referenceId,
  sortOrder,
  editedSortKey,
  disabled,
  onSortKeyChange,
  onConfirmSortKey,
  onCancelSortKey,
  onRemove,
}: CourseModuleContentCardProps) {
  const [imageError, setImageError] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const handleRemoveClick = () => {
    setShowConfirm(true)
  }

  const handleConfirm = () => {
    onRemove(referenceId)
    setShowConfirm(false)
  }

  const handleCancel = () => {
    setShowConfirm(false)
  }

  // Get chip label and class based on content reference type
  const getChipLabel = () => {
    if (contentReferenceType === CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE) {
      return 'Practice'
    }
    if (contentReferenceType === CONTENT_REFERENCE_TYPE_COURSE_MODULE_OTHER) {
      return 'Other'
    }
    return contentReferenceType
  }

  const getChipClass = () =>
    contentReferenceType === CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE
      ? 'language-chip--german'
      : 'language-chip--french'

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card className="course-module-content-section__card">
        <CardContent className="course-module-content-section__card-content">
          {/* Content Reference Type Chip and Sort Order */}
          <Box className="course-module-content-section__chip-row">
            <Box className="course-module-content-section__sort-order-container">
              <TextField
                value={editedSortKey !== undefined ? editedSortKey : sortOrder}
                onChange={(e) => onSortKeyChange(referenceId, e.target.value)}
                disabled={disabled}
                type="number"
                size="small"
                className="course-module-content-section__sort-order-input"
                sx={{
                  width: '60px',
                  '& .MuiInputBase-input': {
                    padding: '6px 8px',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                  },
                }}
              />
              {editedSortKey !== undefined && (
                <Box className="course-module-content-section__sort-order-buttons">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => onConfirmSortKey(referenceId)}
                    className="course-module-content-section__sort-order-button"
                    sx={{
                      padding: '2px',
                      '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' },
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onCancelSortKey(referenceId)}
                    className="course-module-content-section__sort-order-button"
                    sx={{
                      padding: '2px',
                      '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
            {contentReferenceType && (
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {published !== undefined && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: published ? '#4caf50' : '#f44336',
                        }}
                      />
                    )}
                    {getChipLabel()}
                  </Box>
                }
                className={`language-chip course-module-content-section__type-chip ${getChipClass()}`}
                size="small"
              />
            )}
          </Box>

          {/* Thumbnail image with overlay metadata */}
          <Box className="course-module-content-section__thumbnail">
            {thumbnailUrl && !imageError ? (
              <img
                src={thumbnailUrl}
                alt={contentTitle}
                className="course-module-content-section__thumbnail-image"
                onError={() => setImageError(true)}
              />
            ) : (
              <Typography variant="body2" className="course-module-content-section__no-image-text">
                No Image
              </Typography>
            )}
            {/* Content ID and Type overlay */}
            <Box className="course-module-content-section__metadata">
              {contentIdNumber !== undefined && (
                <Typography
                  variant="caption"
                  className="course-module-content-section__metadata-item course-module-content-section__metadata-item--left"
                >
                  {contentIdNumber}
                </Typography>
              )}
              {contentType && (
                <Typography
                  variant="caption"
                  className="course-module-content-section__metadata-item course-module-content-section__metadata-item--right"
                >
                  {contentType}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Title - Link to content detail page */}
          {contentId ? (
            <Link
              href={
                contentType === CONTENT_TYPE_VIDEO
                  ? `${CONTENT_ROOTS.V2}/${contentId}`
                  : `/content/${contentId}`
              }
              target="blank"
              absolute
            >
              <Typography
                variant="body1"
                className="course-module-content-section__title course-module-content-section__title-link"
              >
                {contentTitle}
              </Typography>
            </Link>
          ) : (
            <Typography variant="body1" className="course-module-content-section__title">
              {contentTitle}
            </Typography>
          )}

          {/* Remove button or Confirm/Cancel buttons */}
          {!disabled && (
            <>
              {!showConfirm ? (
                <MuiButton
                  variant="contained"
                  color="error"
                  onClick={handleRemoveClick}
                  className="course-module-content-section__remove-button"
                >
                  Remove
                </MuiButton>
              ) : (
                <Box className="course-module-content-section__confirm-buttons">
                  <MuiButton
                    variant="contained"
                    color="error"
                    onClick={handleConfirm}
                    className="course-module-content-section__confirm-button"
                  >
                    Confirm
                  </MuiButton>
                  <MuiButton
                    variant="outlined"
                    onClick={handleCancel}
                    className="course-module-content-section__cancel-button"
                  >
                    Cancel
                  </MuiButton>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Grid>
  )
}

export default CourseModuleContentCard
